import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { contactInfo } from '../mockData';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const location = useLocation();
  const [activeForm, setActiveForm] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Workforce Request Form State
  const [workforceForm, setWorkforceForm] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    industry: '',
    workersNeeded: '',
    startDate: '',
    location: '',
    description: ''
  });

  // Supplier Registration Form State
  const [supplierForm, setSupplierForm] = useState({
    companyName: '',
    abn: '',
    contactName: '',
    email: '',
    phone: '',
    services: '',
    licences: '',
    insurance: '',
    message: ''
  });

  // General Enquiry Form State
  const [generalForm, setGeneralForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  // Detect form type from URL query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const formType = params.get('form');
    if (formType === 'workforce') {
      setActiveForm('workforce');
    } else if (formType === 'supplier') {
      setActiveForm('supplier');
    }
  }, [location]);

  const handleWorkforceSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/workforce-requests`, workforceForm);
      
      if (response.data.success) {
        toast.success('Workforce request submitted successfully! We\'ll contact you within 24 hours.');
        setWorkforceForm({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          industry: '',
          workersNeeded: '',
          startDate: '',
          location: '',
          description: ''
        });
      }
    } catch (error) {
      console.error('Error submitting workforce request:', error);
      toast.error('Failed to submit request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/supplier-registrations`, supplierForm);
      
      if (response.data.success) {
        toast.success('Supplier registration submitted successfully! Our team will review and contact you soon.');
        setSupplierForm({
          companyName: '',
          abn: '',
          contactName: '',
          email: '',
          phone: '',
          services: '',
          licences: '',
          insurance: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting supplier registration:', error);
      toast.error('Failed to submit registration. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/general-enquiries`, generalForm);
      
      if (response.data.success) {
        toast.success('Enquiry submitted successfully! We\'ll get back to you soon.');
        setGeneralForm({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting general enquiry:', error);
      toast.error('Failed to submit enquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#1a1c1b', minHeight: '100vh' }}>
      {/* Hero Section */}
      <section style={{
        padding: '120px 40px 80px',
        backgroundColor: '#302f2c',
        borderBottom: '1px solid rgba(63, 72, 22, 0.5)'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 0.95,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '24px'
          }}>
            Contact Us
          </h1>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#dfddd6',
            maxWidth: '50rem',
            margin: '0 auto 16px'
          }}>
            Get in touch with Australia's trusted workforce aggregator
          </p>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1rem',
            lineHeight: 1.6,
            color: '#888680',
            maxWidth: '50rem',
            margin: '0 auto'
          }}>
            All enquiries will be sent to {contactInfo.email}
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section style={{
        padding: '60px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '32px'
        }}>
          <div style={{
            backgroundColor: '#302f2c',
            padding: '32px',
            borderRadius: '8px',
            border: '1px solid rgba(63, 72, 22, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px'
          }}>
            <MapPin size={32} style={{ color: '#d9fb06', flexShrink: 0 }} />
            <div>
              <h3 style={{
                fontFamily: 'Arial, sans-serif',
                fontWeight: 700,
                fontSize: '1.1rem',
                color: '#d9fb06',
                marginBottom: '8px',
                textTransform: 'uppercase'
              }}>
                Location
              </h3>
              <p style={{
                fontFamily: 'Inter, Arial, sans-serif',
                fontSize: '1rem',
                color: '#dfddd6',
                margin: 0
              }}>
                {contactInfo.location}
              </p>
              <p style={{
                fontFamily: 'Inter, Arial, sans-serif',
                fontSize: '0.9rem',
                color: '#888680',
                marginTop: '4px'
              }}>
                {contactInfo.expansion}
              </p>
            </div>
          </div>

          <div style={{
            backgroundColor: '#302f2c',
            padding: '32px',
            borderRadius: '8px',
            border: '1px solid rgba(63, 72, 22, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px'
          }}>
            <Mail size={32} style={{ color: '#d9fb06', flexShrink: 0 }} />
            <div>
              <h3 style={{
                fontFamily: 'Arial, sans-serif',
                fontWeight: 700,
                fontSize: '1.1rem',
                color: '#d9fb06',
                marginBottom: '8px',
                textTransform: 'uppercase'
              }}>
                Email
              </h3>
              <a
                href={`mailto:${contactInfo.email}`}
                style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '1rem',
                  color: '#dfddd6',
                  textDecoration: 'none'
                }}
              >
                {contactInfo.email}
              </a>
            </div>
          </div>

          {contactInfo.phone && (
            <div style={{
              backgroundColor: '#302f2c',
              padding: '32px',
              borderRadius: '8px',
              border: '1px solid rgba(63, 72, 22, 0.5)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px'
            }}>
              <Phone size={32} style={{ color: '#d9fb06', flexShrink: 0 }} />
              <div>
                <h3 style={{
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#d9fb06',
                  marginBottom: '8px',
                  textTransform: 'uppercase'
                }}>
                  Phone
                </h3>
                <p style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '1rem',
                  color: '#dfddd6',
                  margin: 0
                }}>
                  {contactInfo.phone}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Form Tabs */}
      <section style={{
        padding: '60px 40px 96px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '60rem',
          margin: '0 auto'
        }}>
          {/* Tab Buttons */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '40px',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {[
              { id: 'workforce', label: 'Request Workforce' },
              { id: 'supplier', label: 'Become a Supplier' },
              { id: 'general', label: 'General Enquiry' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveForm(tab.id)}
                style={{
                  background: activeForm === tab.id ? '#d9fb06' : 'transparent',
                  color: activeForm === tab.id ? '#1a1c1b' : '#d9fb06',
                  border: `2px solid #d9fb06`,
                  borderRadius: '10rem',
                  padding: '1em 2em',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Workforce Request Form */}
          {activeForm === 'workforce' && (
            <form onSubmit={handleWorkforceSubmit} style={{
              backgroundColor: '#302f2c',
              padding: '48px',
              borderRadius: '8px',
              border: '1px solid rgba(63, 72, 22, 0.5)'
            }}>
              <h2 style={{
                fontFamily: 'Arial, sans-serif',
                fontWeight: 700,
                fontSize: '1.8rem',
                color: '#d9fb06',
                marginBottom: '32px',
                textTransform: 'uppercase'
              }}>
                Request Workforce
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Company Name *
                  </label>
                  <Input
                    type="text"
                    required
                    value={workforceForm.companyName}
                    onChange={(e) => setWorkforceForm({ ...workforceForm, companyName: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Contact Name *
                  </label>
                  <Input
                    type="text"
                    required
                    value={workforceForm.contactName}
                    onChange={(e) => setWorkforceForm({ ...workforceForm, contactName: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Email *
                  </label>
                  <Input
                    type="email"
                    required
                    value={workforceForm.email}
                    onChange={(e) => setWorkforceForm({ ...workforceForm, email: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Phone *
                  </label>
                  <Input
                    type="tel"
                    required
                    value={workforceForm.phone}
                    onChange={(e) => setWorkforceForm({ ...workforceForm, phone: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Industry *
                  </label>
                  <Input
                    type="text"
                    required
                    value={workforceForm.industry}
                    onChange={(e) => setWorkforceForm({ ...workforceForm, industry: e.target.value })}
                    placeholder="e.g., Construction, Logistics"
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Workers Needed *
                  </label>
                  <Input
                    type="text"
                    required
                    value={workforceForm.workersNeeded}
                    onChange={(e) => setWorkforceForm({ ...workforceForm, workersNeeded: e.target.value })}
                    placeholder="e.g., 10-15"
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Start Date *
                  </label>
                  <Input
                    type="date"
                    required
                    value={workforceForm.startDate}
                    onChange={(e) => setWorkforceForm({ ...workforceForm, startDate: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Location *
                  </label>
                  <Input
                    type="text"
                    required
                    value={workforceForm.location}
                    onChange={(e) => setWorkforceForm({ ...workforceForm, location: e.target.value })}
                    placeholder="City, State"
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '24px' }}>
                <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                  Project Description *
                </label>
                <Textarea
                  required
                  value={workforceForm.description}
                  onChange={(e) => setWorkforceForm({ ...workforceForm, description: e.target.value })}
                  rows={5}
                  placeholder="Please provide details about your project and workforce requirements..."
                  style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif', width: '100%' }}
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                style={{
                  background: '#d9fb06',
                  color: '#1a1c1b',
                  border: 'none',
                  borderRadius: '10rem',
                  padding: '1.2em 2em',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  marginTop: '32px',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: isSubmitting ? 0.6 : 1
                }}
              >
                <Send size={20} />
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </form>
          )}

          {/* Supplier Registration Form */}
          {activeForm === 'supplier' && (
            <form onSubmit={handleSupplierSubmit} style={{
              backgroundColor: '#302f2c',
              padding: '48px',
              borderRadius: '8px',
              border: '1px solid rgba(63, 72, 22, 0.5)'
            }}>
              <h2 style={{
                fontFamily: 'Arial, sans-serif',
                fontWeight: 700,
                fontSize: '1.8rem',
                color: '#d9fb06',
                marginBottom: '32px',
                textTransform: 'uppercase'
              }}>
                Supplier Registration
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Company Name *
                  </label>
                  <Input
                    type="text"
                    required
                    value={supplierForm.companyName}
                    onChange={(e) => setSupplierForm({ ...supplierForm, companyName: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    ABN *
                  </label>
                  <Input
                    type="text"
                    required
                    value={supplierForm.abn}
                    onChange={(e) => setSupplierForm({ ...supplierForm, abn: e.target.value })}
                    placeholder="XX XXX XXX XXX"
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Contact Name *
                  </label>
                  <Input
                    type="text"
                    required
                    value={supplierForm.contactName}
                    onChange={(e) => setSupplierForm({ ...supplierForm, contactName: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Email *
                  </label>
                  <Input
                    type="email"
                    required
                    value={supplierForm.email}
                    onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Phone *
                  </label>
                  <Input
                    type="tel"
                    required
                    value={supplierForm.phone}
                    onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Services Offered *
                  </label>
                  <Input
                    type="text"
                    required
                    value={supplierForm.services}
                    onChange={(e) => setSupplierForm({ ...supplierForm, services: e.target.value })}
                    placeholder="e.g., Construction labour, Logistics"
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Licences Held *
                  </label>
                  <Input
                    type="text"
                    required
                    value={supplierForm.licences}
                    onChange={(e) => setSupplierForm({ ...supplierForm, licences: e.target.value })}
                    placeholder="Please list relevant licences"
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Insurance (WC & PL) *
                  </label>
                  <Input
                    type="text"
                    required
                    value={supplierForm.insurance}
                    onChange={(e) => setSupplierForm({ ...supplierForm, insurance: e.target.value })}
                    placeholder="Yes/No - Details"
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '24px' }}>
                <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                  Additional Information
                </label>
                <Textarea
                  value={supplierForm.message}
                  onChange={(e) => setSupplierForm({ ...supplierForm, message: e.target.value })}
                  rows={5}
                  placeholder="Tell us about your company, experience, and why you'd like to partner with Worklink Global..."
                  style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif', width: '100%' }}
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                style={{
                  background: '#d9fb06',
                  color: '#1a1c1b',
                  border: 'none',
                  borderRadius: '10rem',
                  padding: '1.2em 2em',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  marginTop: '32px',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: isSubmitting ? 0.6 : 1
                }}
              >
                <Send size={20} />
                {isSubmitting ? 'Submitting...' : 'Submit Registration'}
              </Button>
            </form>
          )}

          {/* General Enquiry Form */}
          {activeForm === 'general' && (
            <form onSubmit={handleGeneralSubmit} style={{
              backgroundColor: '#302f2c',
              padding: '48px',
              borderRadius: '8px',
              border: '1px solid rgba(63, 72, 22, 0.5)'
            }}>
              <h2 style={{
                fontFamily: 'Arial, sans-serif',
                fontWeight: 700,
                fontSize: '1.8rem',
                color: '#d9fb06',
                marginBottom: '32px',
                textTransform: 'uppercase'
              }}>
                General Enquiry
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Name *
                  </label>
                  <Input
                    type="text"
                    required
                    value={generalForm.name}
                    onChange={(e) => setGeneralForm({ ...generalForm, name: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Email *
                  </label>
                  <Input
                    type="email"
                    required
                    value={generalForm.email}
                    onChange={(e) => setGeneralForm({ ...generalForm, email: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Phone
                  </label>
                  <Input
                    type="tel"
                    value={generalForm.phone}
                    onChange={(e) => setGeneralForm({ ...generalForm, phone: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                    Subject *
                  </label>
                  <Input
                    type="text"
                    required
                    value={generalForm.subject}
                    onChange={(e) => setGeneralForm({ ...generalForm, subject: e.target.value })}
                    style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif' }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '24px' }}>
                <label style={{ display: 'block', fontFamily: 'Inter, Arial, sans-serif', fontSize: '0.9rem', color: '#d9fb06', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                  Message *
                </label>
                <Textarea
                  required
                  value={generalForm.message}
                  onChange={(e) => setGeneralForm({ ...generalForm, message: e.target.value })}
                  rows={6}
                  placeholder="How can we help you?"
                  style={{ backgroundColor: '#1a1c1b', border: '1px solid rgba(63, 72, 22, 0.5)', color: '#dfddd6', padding: '12px', borderRadius: '8px', fontFamily: 'Inter, Arial, sans-serif', width: '100%' }}
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                style={{
                  background: '#d9fb06',
                  color: '#1a1c1b',
                  border: 'none',
                  borderRadius: '10rem',
                  padding: '1.2em 2em',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  marginTop: '32px',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: isSubmitting ? 0.6 : 1
                }}
              >
                <Send size={20} />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Contact;
